// src/Dashboard/pages/user/DashboardUser/components/PendingPaymentsPanel.jsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaMoneyBillAlt, FaRegCreditCard } from "react-icons/fa";
import { toast } from "react-toastify";

import { getReglementsByUser } from "../../../../../services/achatsServices";
import { toAbsoluteArticlePhotoUrl } from "../../../../../services/articlesPhotoServices";

import EmptyState from "./EmptyState";
import {
  DASHBOARD_ROUTES,
  formatDateLongFR,
  formatEUR,
  getPaymentStatusClass,
  isPendingReglement,
  sortByDateAsc,
} from "../utils/dashboardUserUtils";

export default function PendingPaymentsPanel({ userId, onCounts }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const prevCountRef = useRef(-1);

  const load = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);

      const list = await getReglementsByUser(userId);
      const pending = sortByDateAsc(
        (Array.isArray(list) ? list : []).filter(isPendingReglement),
        "date_achat"
      );

      setRows(pending);
    } catch (error) {
      console.error("[PendingPaymentsPanel] load error:", error);
      toast.error("Impossible de charger les règlements.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!onCounts) return;
    if (prevCountRef.current === rows.length) return;

    prevCountRef.current = rows.length;
    onCounts({ total: rows.length });
  }, [rows.length, onCounts]);

  if (loading) {
    return (
      <div className="grid gap-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="h-24 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (!rows.length) {
    return (
      <EmptyState
        icon={FaMoneyBillAlt}
        title="Aucun règlement en attente"
        description="Tous vos achats et règlements semblent à jour."
      />
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const article = row.Article || null;
        const title =
          article?.nom ||
          row.message_achat ||
          row.type_achat ||
          "Règlement inscription";

        const rawImg = article?.photo_url || "/img/no-image.jpg";
        const imgSrc = article?.photo_url
          ? toAbsoluteArticlePhotoUrl(rawImg)
          : rawImg;

        return (
          <article
            key={row.id}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="flex flex-col sm:flex-row">
              <div className="h-40 bg-slate-100 sm:h-auto sm:w-36 dark:bg-slate-950">
                <img
                  src={imgSrc}
                  alt={title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.src = "/img/no-image.jpg";
                  }}
                />
              </div>

              <div className="flex flex-1 flex-col gap-3 p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="font-bold text-slate-900 dark:text-white">
                      {title}
                    </h3>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Achat du {formatDateLongFR(row.date_achat)}
                    </p>
                  </div>

                  <div className="text-left sm:text-right">
                    <p className="text-xl font-bold text-slate-900 dark:text-white">
                      {formatEUR(row.prix_total)}
                    </p>
                    <span
                      className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${getPaymentStatusClass(
                        row.statut_reglement
                      )}`}
                    >
                      {row.statut_reglement || "—"}
                    </span>
                  </div>
                </div>

                {row.message_achat && (
                  <p className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600 dark:bg-slate-800/60 dark:text-slate-300">
                    {row.message_achat}
                  </p>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => navigate(DASHBOARD_ROUTES.payments)}
                    className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-violet-700"
                  >
                    <FaRegCreditCard />
                    Régler maintenant
                  </button>
                </div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}